import { getContenidos, getContenidosCache, getContenidoSlug, refreshContenidosCacheBackEnd } from "../../../services/backend/contentBackendService";
import { normalizeSlug } from "./pageConfig";
import { ensureCatalogAuth } from "../../../services/backend/authService";
import { getCompany } from "../../../config/companyConfig";
import logger from "../../../shared/utils/logger";

let _pagesData = null;
let _initPromise = null;
let _activeContenidoId = null;
let _version = 0;
const _listeners = new Set();
const _slugCache = new Map();

const _notify = () => {
  _version += 1;
  _listeners.forEach((listener) => listener(_version));
};

const toContentFormat = (raw) => {
  if (raw?.companies) return raw;
  const landingId = raw?.defaultLandingId || getCompany().id;
  return {
    defaultLandingId: landingId,
    companies: {
      [landingId]: {
        title: raw?.title || landingId,
        pages: Array.isArray(raw?.pages) ? raw.pages : [],
      },
    },
  };
};

const ContentService = {
  init() {
    if (!_initPromise) {
      _initPromise = this._fetchFromCache();
    }
    return _initPromise;
  },

  initAdmin() {
    _initPromise = this._fetch();
    return _initPromise;
  },

  _fetchFromCache() {
    return getContenidosCache()
      .then((data) => {
        if (data?.error === false && data?.contenido?.json) {
          try {
            const parsed = JSON.parse(data.contenido.json);
            _pagesData = toContentFormat(parsed);
            _activeContenidoId = data.contenido.id;
          } catch (e) {
            logger.error("Error parsing contenido cache:", e);
            _pagesData = {};
          }
        } else {
          _pagesData = {};
        }
        _notify();
        return _pagesData;
      })
      .catch((err) => {
        logger.error("Error loading content cache:", err);
        _pagesData = {};
        _notify();
        return {};
      });
  },

  _fetch() {
    return getContenidos()
      .then((data) => {
        const activo = Array.isArray(data?.contenidos)
          ? data.contenidos.find((c) => c.activo === true)
          : null;
        _activeContenidoId = activo?.id || null;
        if (activo?.json) {
          try {
            _pagesData = toContentFormat(JSON.parse(activo.json));
          } catch (e) {
            logger.error("Error parsing contenido json:", e);
            _pagesData = {};
          }
        } else {
          _pagesData = {};
        }
        _notify();
        return _pagesData;
      })
      .catch((err) => {
        logger.error("Error loading content:", err);
        _pagesData = {};
        _notify();
        return {};
      });
  },

  async refreshContenidosCache() {
    try {
      await ensureCatalogAuth();
      await refreshContenidosCacheBackEnd();
      await this._fetchFromCache();
      _slugCache.clear();
    } catch (err) {
      logger.error("Error refreshing contenidos cache:", err);
    }
  },

  patchLandingPages(landingId, pages) {
    if (!_pagesData) {
      _pagesData = {};
    }
    if (!_pagesData.companies) {
      _pagesData.companies = {};
    }
    if (!_pagesData.companies[landingId]) {
      _pagesData.companies[landingId] = {};
    }
    _pagesData.companies[landingId].pages = Array.isArray(pages) ? pages : [];
    _notify();
  },

  getActiveContenidoId() {
    return _activeContenidoId;
  },

  getPagesForLanding(landingId) {
    const company = _pagesData?.companies?.[landingId] || null;
    return Array.isArray(company?.pages) ? company.pages : [];
  },

  async getPageBySlugFromBackend(slug) {
    const normalizedSlug = normalizeSlug(slug);
    if (!normalizedSlug) return null;

    if (_slugCache.has(normalizedSlug)) {
      return _slugCache.get(normalizedSlug);
    }

    try {
      const data = await getContenidoSlug(normalizedSlug);
      if (data?.error === false && data?.contenido) {
        const page = {
          slug: normalizeSlug(data.contenido.slug || normalizedSlug),
          navLabel: data.contenido.navLabel || data.contenido.title || normalizedSlug,
          title: data.contenido.title || data.contenido.navLabel || "Página",
          isHome: data.contenido.isHome === true,
          footerColumn: Number.isInteger(data.contenido.footerColumn) ? data.contenido.footerColumn : 0,
          sections: Array.isArray(data.contenido.sections) ? data.contenido.sections : [],
        };
        _slugCache.set(normalizedSlug, page);
        return page;
      }
      return null;
    } catch (err) {
      logger.warn(`Backend slug lookup failed for "${normalizedSlug}":`, err);
      return null;
    }
  },

  getVersion() {
    return _version;
  },

  subscribe(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
  },
};

export default ContentService;
